import AsyncStorage from '@react-native-async-storage/async-storage';
let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  TextInput,
  Image,
  ActivityIndicator,
  Alert,
  Switch,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import useProfile from '../../hooks/useProfile';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
export default function CreateCoverLetterScreen({ navigation, route }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_createcoverletterscreen`;
    apiFetch('/api/data/jobs?is_active=true&limit=10').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  // States for cover letter creation
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showAITips, setShowAITips] = useState(true);

  // Cover letter data
  const [coverLetterData, setCoverLetterData] = useState({
    jobTitle: '',
    company: '',
    recipientName: '',
    recipientPosition: '',
    introduction: '',
    body: '',
    closing: '',
    template: 'professional',
    tone: 'professional',
  });

  // Resume data (would come from route params in a real app)
  const resumeData = {
    personalInfo: {
      fullName: 'James Wilson',
      profession: 'Mechanical Engineer',
      email: 'james.wilson@email.com',
      phone: '+44 7700 900123',
      location: 'London, UK',
      linkedin: 'linkedin.com/in/jameswilson',
    },
    experience: [
      {
        title: 'Senior Mechanical Engineer',
        company: 'Rolls-Royce',
        location: 'Derby, UK',
        startDate: '06/2020',
        endDate: 'Present',
        description:
          'Led a team of 5 engineers in designing turbine components. Implemented cost-saving measures that reduced production expenses by 15%. Collaborated with cross-functional teams to optimize manufacturing processes.',
      },
    ],
    skills: [
      'CAD (SolidWorks, AutoCAD)',
      'Finite Element Analysis (FEA)',
      'Project Management',
      'Thermal Analysis',
      'Prototyping',
      'GD&T',
      'MATLAB',
      'Team Leadership',
      'Technical Documentation',
      'Problem Solving',
    ],
  };

  // Templates for cover letter
  const templates = [
    { id: 'professional', name: 'Professional', icon: 'briefcase-outline' },
    { id: 'modern', name: 'Modern', icon: 'trending-up' },
    { id: 'creative', name: 'Creative', icon: 'bulb-outline' },
    { id: 'technical', name: 'Technical', icon: 'construct-outline' },
  ];

  // Tones for cover letter
  const tones = [
    {
      id: 'professional',
      name: 'Professional',
      description: 'Formal and business-like',
    },
    {
      id: 'confident',
      name: 'Confident',
      description: 'Assertive and self-assured',
    },
    {
      id: 'enthusiastic',
      name: 'Enthusiastic',
      description: 'Passionate and eager',
    },
    {
      id: 'technical',
      name: 'Technical',
      description: 'Detailed and specialized',
    },
  ];

  // AI tips for each step
  const aiTips = {
    1: [
      'Include the specific job title exactly as it appears in the listing',
      "Research the company to find the hiring manager's name if possible",
      'If you can\'t find a specific name, use "Dear Hiring Manager" or "Dear [Department] Team"',
    ],
    2: [
      'Choose a template that matches the company culture',
      'Technical roles often prefer more traditional templates',
      'Creative roles may appreciate modern or creative designs',
    ],
    3: [
      'The tone should match both the company culture and your personality',
      'Technical tone works well for engineering positions',
      'Confident tone is good for leadership roles',
    ],
    4: [
      'Your introduction should grab attention and state your interest in the position',
      'Mention how you found the job opening',
      "Include a brief statement about why you're a good fit",
    ],
    5: [
      'Focus on 2-3 relevant achievements from your resume',
      'Use specific numbers and metrics when possible',
      'Connect your experience directly to the job requirements',
      'Show how your skills will benefit the company',
    ],
    6: [
      'Express enthusiasm for the opportunity to interview',
      'Thank the reader for their consideration',
      'Include a call to action',
      'Keep the closing concise and professional',
    ],
  };

  // Update cover letter data
  const updateCoverLetterData = (field, value) => {
    setCoverLetterData({
      ...coverLetterData,
      [field]: value,
    });
  };

  // Generate with AI
  const generateWithAI = section => {
    setIsGenerating(true);

    // Simulate AI generation
    setTimeout(() => {
      switch (section) {
        case 'introduction':
          updateCoverLetterData(
            'introduction',
            `I am writing to express my interest in the ${coverLetterData.jobTitle} position at ${coverLetterData.company}. With over 5 years of experience in mechanical engineering and a proven track record of leading teams and optimizing processes, I am confident that my technical expertise and leadership skills make me an ideal candidate for this role.`
          );
          break;

        case 'body':
          updateCoverLetterData(
            'body',
            `During my time at Rolls-Royce, I led a team of 5 engineers in designing critical turbine components, resulting in a 15% reduction in production expenses while maintaining the highest quality standards. My expertise in CAD software, Finite Element Analysis, and thermal systems has enabled me to develop innovative solutions to complex engineering challenges.\n\nI am particularly drawn to ${coverLetterData.company}'s commitment to sustainable engineering practices and cutting-edge technology. My experience with optimizing manufacturing processes and implementing lean principles aligns perfectly with your company's focus on efficiency and innovation. I am confident that my technical skills and collaborative approach would make me a valuable addition to your engineering team.`
          );
          break;

        case 'closing':
          updateCoverLetterData(
            'closing',
            `Thank you for considering my application. I am excited about the opportunity to bring my skills and experience to ${coverLetterData.company} and would welcome the chance to discuss how I can contribute to your team's success. I look forward to speaking with you soon about this exciting opportunity.`
          );
          break;

        default:
          break;
      }

      setIsGenerating(false);
    }, 2000);
  };

  // Next step
  const nextStep = () => {
    if (step < 7) {
      setStep(step + 1);
    } else {
      // Final step - save and preview
      saveAndPreview();
    }
  };

  // Previous step
  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  // Save and preview
  const saveAndPreview = () => {
    setIsLoading(true);

    // Simulate saving
    setTimeout(() => {
      setIsLoading(false);

      // Try navigating to the dedicated preview screen; if it doesn't exist
      // in the navigator (no screen file yet), fall back to an inline preview Alert.
      try {
        const state = navigation.getState && navigation.getState();
        const allRoutes = [];
        const collect = (s) => {
          if (!s || !s.routeNames) return;
          s.routeNames.forEach(n => allRoutes.push(n));
          (s.routes || []).forEach(r => r.state && collect(r.state));
        };
        collect(state);

        if (allRoutes.includes('CoverLetterPreview')) {
          navigation.navigate('CoverLetterPreview', { coverLetterData });
          return;
        }
      } catch {}

      // Fallback: confirm save inline
      const previewBody = (coverLetterData?.body || coverLetterData?.content || '').toString().slice(0, 400);
      Alert.alert(
        'Cover Letter Saved',
        previewBody
          ? `Preview:\n\n${previewBody}${previewBody.length === 400 ? '…' : ''}\n\nYou can find it later in My Documents.`
          : 'You can find your cover letter later in My Documents.',
        [
          { text: 'OK', onPress: () => navigation.goBack() },
        ],
      );
    }, 1500);
  };

  // Check if current step is complete
  const isStepComplete = () => {
    switch (step) {
      case 1:
        return (
          coverLetterData.jobTitle.trim() !== '' &&
          coverLetterData.company.trim() !== ''
        );
      case 2:
        return coverLetterData.template !== '';
      case 3:
        return coverLetterData.tone !== '';
      case 4:
        return coverLetterData.introduction.trim() !== '';
      case 5:
        return coverLetterData.body.trim() !== '';
      case 6:
        return coverLetterData.closing.trim() !== '';
      default:
        return true;
    }
  };

  // Render job details step
  const renderJobDetailsStep = () => (
    <View style={styles.stepContent}>
      <Text style={styles.stepDescription}>
        Enter the job details to personalize your cover letter
      </Text>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Job Title</Text>
        <TextInput
          style={styles.textInput}
          value={coverLetterData.jobTitle}
          onChangeText={text => updateCoverLetterData('jobTitle', text)}
          placeholder="e.g., Senior Mechanical Engineer"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Company Name</Text>
        <TextInput
          style={styles.textInput}
          value={coverLetterData.company}
          onChangeText={text => updateCoverLetterData('company', text)}
          placeholder="e.g., Rolls-Royce"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Recipient's Name (Optional)</Text>
        <TextInput
          style={styles.textInput}
          value={coverLetterData.recipientName}
          onChangeText={text => updateCoverLetterData('recipientName', text)}
          placeholder="e.g., Dr. Jane Smith"
        />
      </View>

      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Recipient's Position (Optional)</Text>
        <TextInput
          style={styles.textInput}
          value={coverLetterData.recipientPosition}
          onChangeText={text =>
            updateCoverLetterData('recipientPosition', text)
          }
          placeholder="e.g., Head of Engineering"
        />
      </View>
    </View>
  );

  // Render template selection step
  const renderTemplateStep = () => (
    <View style={styles.stepContent}>
      <Text style={styles.stepDescription}>
        Choose a template that best represents your professional style
      </Text>

      <View style={styles.templatesContainer}>
        {templates.map(template => (
          <TouchableOpacity
            key={template.id}
            style={[
              styles.templateItem,
              coverLetterData.template === template.id &&
                styles.selectedTemplateItem,
            ]}
            onPress={() => updateCoverLetterData('template', template.id)}
          >
            <View
              style={[
                styles.templateIcon,
                coverLetterData.template === template.id &&
                  styles.selectedTemplateIcon,
              ]}
            >
              <Ionicons
                name={template.icon}
                size={32}
                color={
                  coverLetterData.template === template.id
                    ? '#FFFFFF'
                    : '#0e4e96'
                }
              />
            </View>
            <Text
              style={[
                styles.templateName,
                coverLetterData.template === template.id &&
                  styles.selectedTemplateName,
              ]}
            >
              {template.name}
            </Text>
          </TouchableOpacity>
        ))}
      </View>

      <View style={styles.templatePreview}>
        <Text style={styles.previewLabel}>Preview</Text>
        <View style={styles.previewContainer}>
          <View style={styles.previewHeader}>
            <View style={[styles.previewHeaderLine, { width: '60%' }]} />
            <View style={[styles.previewHeaderLine, { width: '80%' }]} />
            <View style={[styles.previewHeaderLine, { width: '40%' }]} />
          </View>

          <View style={styles.previewBody}>
            <View style={[styles.previewBodyLine, { width: '90%' }]} />
            <View style={[styles.previewBodyLine, { width: '85%' }]} />
            <View style={[styles.previewBodyLine, { width: '92%' }]} />
            <View style={[styles.previewBodyLine, { width: '88%' }]} />
            <View style={[styles.previewBodyLine, { width: '75%' }]} />
          </View>

          <View style={styles.previewFooter}>
            <View style={[styles.previewFooterLine, { width: '50%' }]} />
            <View style={[styles.previewFooterLine, { width: '30%' }]} />
          </View>
        </View>
      </View>
    </View>
  );

  // Render tone selection step
  const renderToneStep = () => (
    <View style={styles.stepContent}>
      <Text style={styles.stepDescription}>
        Select the tone that best suits the job and company culture
      </Text>

      <View style={styles.tonesContainer}>
        {tones.map(tone => (
          <TouchableOpacity
            key={tone.id}
            style={[
              styles.toneItem,
              coverLetterData.tone === tone.id && styles.selectedToneItem,
            ]}
            onPress={() => updateCoverLetterData('tone', tone.id)}
          >
            <View style={styles.toneHeader}>
              <Text
                style={[
                  styles.toneName,
                  coverLetterData.tone === tone.id && styles.selectedToneName,
                ]}
              >
                {tone.name}
              </Text>
              {coverLetterData.tone === tone.id && (
                <Ionicons name="checkmark-circle" size={20} color="#0e4e96" />
              )}
            </View>
            <Text style={styles.toneDescription}>{tone.description}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <View style={styles.toneExample}>
        <Text style={styles.exampleLabel}>Example Phrase:</Text>
        <View style={styles.exampleContainer}>
          <Text style={styles.exampleText}>
            {coverLetterData.tone === 'professional' &&
              'I am writing to express my interest in the position at your esteemed organization.'}
            {coverLetterData.tone === 'confident' &&
              'My proven track record and specialized skills make me the ideal candidate for this position.'}
            {coverLetterData.tone === 'enthusiastic' &&
              "I'm thrilled about the opportunity to bring my passion and expertise to your innovative team!"}
            {coverLetterData.tone === 'technical' &&
              'My experience implementing finite element analysis methodologies aligns with the technical requirements specified in the job description.'}
          </Text>
        </View>
      </View>
    </View>
  );

  // Render introduction step
  const renderIntroductionStep = () => (
    <View style={styles.stepContent}>
      <Text style={styles.stepDescription}>
        Create an engaging introduction that captures attention
      </Text>

      <View style={styles.inputGroup}>
        <View style={styles.labelContainer}>
          <Text style={styles.inputLabel}>Introduction</Text>
          <TouchableOpacity
            style={styles.generateButton}
            onPress={() => generateWithAI('introduction')}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <ActivityIndicator size={20} color="#FFFFFF" />
            ) : (
              <>
                <Ionicons name="flash" size={16} color="#FFFFFF" />
                <Text style={styles.generateButtonText}>Generate</Text>
              </>
            )}
          </TouchableOpacity>
        </View>

        <TextInput
          style={[styles.textInput, styles.textArea]}
          value={coverLetterData.introduction}
          onChangeText={text => updateCoverLetterData('introduction', text)}
          placeholder="Write an introduction that states your interest in the position and briefly mentions your qualifications..."
          multiline
          numberOfLines={6}
          textAlignVertical="top"
        />
      </View>

      <View style={styles.introductionExample}>
        <Text style={styles.exampleLabel}>Example Introduction:</Text>
        <View style={styles.exampleContainer}>
          <Text style={styles.exampleText}>
            I am writing to express my interest in the [Job Title] position at
            [Company Name]. With [X] years of experience in [relevant field] and
            a proven track record of [key achievement], I am confident that my
            skills and expertise make me an ideal candidate for this role.
          </Text>
        </View>
      </View>
    </View>
  );

  // Render body step
  const renderBodyStep = () => (
    <View style={styles.stepContent}>
      <Text style={styles.stepDescription}>
        Highlight your relevant experience and skills
      </Text>

      <View style={styles.inputGroup}>
        <View style={styles.labelContainer}>
          <Text style={styles.inputLabel}>Body</Text>
          <TouchableOpacity
            style={styles.generateButton}
            onPress={() => generateWithAI('body')}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <ActivityIndicator size={20} color="#FFFFFF" />
            ) : (
              <>
                <Ionicons name="flash" size={16} color="#FFFFFF" />
                <Text style={styles.generateButtonText}>Generate</Text>
              </>
            )}
          </TouchableOpacity>
        </View>

        <TextInput
          style={[styles.textInput, styles.textArea, { height: 200 }]}
          value={coverLetterData.body}
          onChangeText={text => updateCoverLetterData('body', text)}
          placeholder="Describe your relevant experience, skills, and achievements. Connect your background to the job requirements and explain why you're a good fit for the role..."
          multiline
          numberOfLines={12}
          textAlignVertical="top"
        />
      </View>

      <View style={styles.relevantSkills}>
        <Text style={styles.relevantSkillsTitle}>Your Relevant Skills:</Text>
        <View style={styles.skillsContainer}>
          {resumeData.skills.slice(0, 5).map((skill, index) => (
            <View key={index} style={styles.skillBadge}>
              <Text style={styles.skillText}>{skill}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );

  // Render closing step
  const renderClosingStep = () => (
    <View style={styles.stepContent}>
      <Text style={styles.stepDescription}>
        Create a strong closing that encourages further action
      </Text>

      <View style={styles.inputGroup}>
        <View style={styles.labelContainer}>
          <Text style={styles.inputLabel}>Closing</Text>
          <TouchableOpacity
            style={styles.generateButton}
            onPress={() => generateWithAI('closing')}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <ActivityIndicator size={20} color="#FFFFFF" />
            ) : (
              <>
                <Ionicons name="flash" size={16} color="#FFFFFF" />
                <Text style={styles.generateButtonText}>Generate</Text>
              </>
            )}
          </TouchableOpacity>
        </View>

        <TextInput
          style={[styles.textInput, styles.textArea]}
          value={coverLetterData.closing}
          onChangeText={text => updateCoverLetterData('closing', text)}
          placeholder="Write a closing paragraph that thanks the reader, expresses your interest in an interview, and includes a call to action..."
          multiline
          numberOfLines={6}
          textAlignVertical="top"
        />
      </View>

      <View style={styles.closingExample}>
        <Text style={styles.exampleLabel}>Example Closing:</Text>
        <View style={styles.exampleContainer}>
          <Text style={styles.exampleText}>
            Thank you for considering my application. I am excited about the
            opportunity to bring my skills and experience to [Company Name] and
            would welcome the chance to discuss how I can contribute to your
            team's success. I look forward to speaking with you soon.
          </Text>
        </View>
      </View>
    </View>
  );

  // Render review step
  const renderReviewStep = () => (
    <View style={styles.stepContent}>
      <Text style={styles.stepDescription}>
        Review your cover letter before finalizing
      </Text>

      <View style={styles.reviewCard}>
        <View style={styles.reviewHeader}>
          <Text style={styles.reviewTitle}>Cover Letter Summary</Text>
        </View>

        <View style={styles.reviewContent}>
          <View style={styles.reviewItem}>
            <Text style={styles.reviewLabel}>Job Title:</Text>
            <Text style={styles.reviewValue}>{coverLetterData.jobTitle}</Text>
          </View>

          <View style={styles.reviewItem}>
            <Text style={styles.reviewLabel}>Company:</Text>
            <Text style={styles.reviewValue}>{coverLetterData.company}</Text>
          </View>

          <View style={styles.reviewItem}>
            <Text style={styles.reviewLabel}>Template:</Text>
            <Text style={styles.reviewValue}>
              {templates.find(t => t.id === coverLetterData.template)?.name ||
                'Professional'}
            </Text>
          </View>

          <View style={styles.reviewItem}>
            <Text style={styles.reviewLabel}>Tone:</Text>
            <Text style={styles.reviewValue}>
              {tones.find(t => t.id === coverLetterData.tone)?.name ||
                'Professional'}
            </Text>
          </View>

          <View style={styles.reviewDivider} />

          <View style={styles.reviewSection}>
            <Text style={styles.reviewSectionTitle}>Introduction</Text>
            <Text style={styles.reviewSectionContent} numberOfLines={3}>
              {coverLetterData.introduction}
            </Text>
            <TouchableOpacity
              style={styles.editButton}
              onPress={() => setStep(4)}
            >
              <Ionicons name="pencil" size={16} color="#0e4e96" />
              <Text style={styles.editButtonText}>Edit</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.reviewSection}>
            <Text style={styles.reviewSectionTitle}>Body</Text>
            <Text style={styles.reviewSectionContent} numberOfLines={3}>
              {coverLetterData.body}
            </Text>
            <TouchableOpacity
              style={styles.editButton}
              onPress={() => setStep(5)}
            >
              <Ionicons name="pencil" size={16} color="#0e4e96" />
              <Text style={styles.editButtonText}>Edit</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.reviewSection}>
            <Text style={styles.reviewSectionTitle}>Closing</Text>
            <Text style={styles.reviewSectionContent} numberOfLines={3}>
              {coverLetterData.closing}
            </Text>
            <TouchableOpacity
              style={styles.editButton}
              onPress={() => setStep(6)}
            >
              <Ionicons name="pencil" size={16} color="#0e4e96" />
              <Text style={styles.editButtonText}>Edit</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );

  // Render AI tips
  const renderAITips = () => {
    if (!showAITips) return null;

    return (
      <View style={styles.aiTipsContainer}>
        <View style={styles.aiTipsHeader}>
          <View style={styles.aiTipsTitle}>
            <Ionicons name="bulb" size={20} color="#fdb913" />
            <Text style={styles.aiTipsTitleText}>AI Tips</Text>
          </View>

          <TouchableOpacity
            style={styles.aiTipsCloseButton}
            onPress={() => setShowAITips(false)}
          >
            <Ionicons name="close" size={20} color="#64748B" />
          </TouchableOpacity>
        </View>

        <View style={styles.aiTipsList}>
          {aiTips[step].map((tip, index) => (
            <View key={index} style={styles.aiTipItem}>
              <Ionicons name="checkmark-circle" size={16} color="#19abb5" />
              <Text style={styles.aiTipText}>{tip}</Text>
            </View>
          ))}
        </View>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="dark-content" backgroundColor="#F8FAFC" />

      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#0e4e96" />
        </TouchableOpacity>

        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>Create Cover Letter</Text>
        </View>

        <TouchableOpacity style={styles.helpButton}>
          <Ionicons name="help-circle-outline" size={24} color="#0e4e96" />
        </TouchableOpacity>
      </View>

      <View style={styles.progressContainer}>
        <View style={styles.progressBar}>
          <View
            style={[styles.progressFill, { width: `${(step / 7) * 100}%` }]}
          />
        </View>
        <Text style={styles.progressText}>Step {step} of 7</Text>
      </View>

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.container}>
          <View style={styles.stepHeader}>
            <Text style={styles.stepTitle}>
              {step === 1 && 'Job Details'}
              {step === 2 && 'Choose Template'}
              {step === 3 && 'Select Tone'}
              {step === 4 && 'Introduction'}
              {step === 5 && 'Body Content'}
              {step === 6 && 'Closing'}
              {step === 7 && 'Review'}
            </Text>
          </View>

          {renderAITips()}

          {step === 1 && renderJobDetailsStep()}
          {step === 2 && renderTemplateStep()}
          {step === 3 && renderToneStep()}
          {step === 4 && renderIntroductionStep()}
          {step === 5 && renderBodyStep()}
          {step === 6 && renderClosingStep()}
          {step === 7 && renderReviewStep()}
        </View>
      </ScrollView>

      <View style={styles.footer}>
        <View style={styles.aiTipsToggle}>
          <Text style={styles.aiTipsToggleText}>Show AI Tips</Text>
          <Switch
            value={showAITips}
            onValueChange={setShowAITips}
            trackColor={{ false: '#E2E8F0', true: '#0e4e96' }}
            thumbColor={showAITips ? '#FFFFFF' : '#FFFFFF'}
          />
        </View>

        <View style={styles.footerButtons}>
          {step > 1 && (
            <TouchableOpacity style={styles.backStepButton} onPress={prevStep}>
              <Ionicons name="arrow-back" size={20} color="#64748B" />
              <Text style={styles.backStepButtonText}>Back</Text>
            </TouchableOpacity>
          )}

          <TouchableOpacity
            style={[
              styles.nextStepButton,
              !isStepComplete() && styles.disabledButton,
            ]}
            onPress={nextStep}
            disabled={!isStepComplete() || isLoading}
          >
            {isLoading ? (
              <ActivityIndicator size={20} color="#FFFFFF" />
            ) : (
              <>
                <Text style={styles.nextStepButtonText}>
                  {step < 7 ? 'Next' : 'Save & Preview'}
                </Text>
                <Ionicons name="arrow-forward" size={20} color="#FFFFFF" />
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  backButton: {
    padding: 8,
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
  },
  helpButton: {
    padding: 8,
  },
  progressContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  progressBar: {
    height: 8,
    backgroundColor: '#E2E8F0',
    borderRadius: 4,
    marginBottom: 8,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#0e4e96',
    borderRadius: 4,
  },
  progressText: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'right',
  },
  scrollView: {
    flex: 1,
  },
  container: {
    padding: 16,
    paddingBottom: 32,
  },
  stepHeader: {
    marginBottom: 16,
  },
  stepTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#0F172A',
  },
  stepDescription: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 16,
  },
  aiTipsContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  aiTipsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  aiTipsTitle: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  aiTipsTitleText: {
    fontSize: 16,
    fontWeight: '700',
    color: '#0F172A',
    marginLeft: 8,
  },
  aiTipsCloseButton: {
    padding: 4,
  },
  aiTipsList: {},
  aiTipItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  aiTipText: {
    fontSize: 14,
    color: '#0F172A',
    marginLeft: 8,
    flex: 1,
  },
  stepContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  inputGroup: {
    marginBottom: 16,
  },
  labelContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 8,
  },
  textInput: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#0F172A',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  textArea: {
    minHeight: 120,
    textAlignVertical: 'top',
  },
  generateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fdb913',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
  },
  generateButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  templatesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  templateItem: {
    width: '48%',
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  selectedTemplateItem: {
    borderColor: '#0e4e96',
    backgroundColor: '#F0F9FF',
  },
  templateIcon: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  selectedTemplateIcon: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  templateName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
  },
  selectedTemplateName: {
    color: '#0e4e96',
  },
  templatePreview: {
    marginTop: 8,
  },
  previewLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 8,
  },
  previewContainer: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  previewHeader: {
    marginBottom: 16,
  },
  previewHeaderLine: {
    height: 8,
    backgroundColor: '#E2E8F0',
    borderRadius: 4,
    marginBottom: 8,
  },
  previewBody: {
    marginBottom: 16,
  },
  previewBodyLine: {
    height: 6,
    backgroundColor: '#E2E8F0',
    borderRadius: 3,
    marginBottom: 6,
  },
  previewFooter: {},
  previewFooterLine: {
    height: 8,
    backgroundColor: '#E2E8F0',
    borderRadius: 4,
    marginBottom: 8,
  },
  tonesContainer: {
    marginBottom: 16,
  },
  toneItem: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  selectedToneItem: {
    borderColor: '#0e4e96',
    backgroundColor: '#F0F9FF',
  },
  toneHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  toneName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
  },
  selectedToneName: {
    color: '#0e4e96',
  },
  toneDescription: {
    fontSize: 14,
    color: '#64748B',
  },
  toneExample: {
    marginTop: 8,
  },
  exampleLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 8,
  },
  exampleContainer: {
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  exampleText: {
    fontSize: 14,
    color: '#0F172A',
    fontStyle: 'italic',
  },
  introductionExample: {
    marginTop: 16,
  },
  closingExample: {
    marginTop: 16,
  },
  relevantSkills: {
    marginTop: 16,
  },
  relevantSkillsTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 8,
  },
  skillsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  skillBadge: {
    backgroundColor: '#F1F5F9',
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    marginRight: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  skillText: {
    fontSize: 12,
    color: '#0F172A',
  },
  reviewCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  reviewHeader: {
    backgroundColor: '#F1F5F9',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  reviewTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#0F172A',
  },
  reviewContent: {
    padding: 16,
  },
  reviewItem: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  reviewLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    width: 100,
  },
  reviewValue: {
    fontSize: 14,
    color: '#0F172A',
    flex: 1,
  },
  reviewDivider: {
    height: 1,
    backgroundColor: '#E2E8F0',
    marginVertical: 16,
  },
  reviewSection: {
    marginBottom: 16,
  },
  reviewSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 8,
  },
  reviewSectionContent: {
    fontSize: 14,
    color: '#0F172A',
    marginBottom: 8,
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
  },
  editButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0e4e96',
    marginLeft: 4,
  },
  footer: {
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  aiTipsToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginBottom: 12,
  },
  aiTipsToggleText: {
    fontSize: 14,
    color: '#0F172A',
    marginRight: 8,
  },
  footerButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  backStepButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F1F5F9',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  backStepButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#64748B',
    marginLeft: 8,
  },
  nextStepButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginLeft: 12,
  },
  nextStepButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginRight: 8,
  },
  disabledButton: {
    backgroundColor: '#94A3B8',
  },
});
